import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { Markdown, TableChipCell } from "@pimo/pimo-components";
import { Currency, Initiative, MonthlyImpact, Phase } from "aint-types";
import {
  getColorsAndTextForInitiativePhaseChips,
  IMPACT_SERIES_IDS_AND_LABELS,
} from "aint-utils";
import React from "react";

import { SupportingDocument } from "../supporting-document";

interface InitiativePopupProps {
  initiative: Initiative | undefined;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
  showInterOeRegionField: boolean;
  showBusinessUnitField: boolean;
  showCategoryField: boolean;
  showOeProgramFields: boolean;
  currency?: Currency;
}

const FixedWidthBox: PimoReactComponent<{
  label: string;
  value: string | boolean | string[] | undefined | null;
}> = ({ label, value }) => {
  return (
    value && (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "330px",
          gap: 1,
        }}
      >
        <Typography variant="subtitle1">{label}</Typography>
        <Typography variant="body2">
          {typeof value === "boolean"
            ? value
              ? "Yes"
              : "No"
            : typeof value === "string"
              ? value
              : value?.join(", ")}
        </Typography>
      </Box>
    )
  );
};

export const InitiativePopup: PimoReactComponent<
  InitiativePopupProps,
  "popup:closed"
> = ({
  initiative = {} as Initiative,
  open,
  setOpen,
  fireEvent,
  showBusinessUnitField,
  showInterOeRegionField,
  showCategoryField,
  showOeProgramFields,
  currency,
}) => {
  const handleClose = () => {
    fireEvent?.("popup:closed");
    setOpen?.(false);
  };
  const theme = useTheme();
  const [isOnPrint, setIsOnPrint] = React.useState(false);
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{ boxShadow: "none", zIndex: 10000 }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "none",
          zIndex: 10000,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            boxShadow: "none",
          },
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box display="flex" alignItems="center">
          <Typography variant="h6">{initiative?.initiativeName}</Typography>
          <Typography
            ml={2}
            p={1}
            py={0.5}
            color="white"
            borderRadius="12px"
            sx={{
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.secondary.contrastText,
            }}
            variant="subtitle1"
          >
            {initiative?.draft ? "Draft" : "Published"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            sx={{
              borderRadius: "4px",
              border: "1px solid",
              "@media print": {
                display: "none",
              },
            }}
            onClick={() => {
              setIsOnPrint(true);
              window.requestIdleCallback(() => window.print(), {
                timeout: 1_000,
              });

              window.onafterprint = () => {
                setIsOnPrint(false);
              };
            }}
          >
            Download PDF
          </Button>
          <IconButton
            sx={{
              "@media print": {
                display: "none",
              },
            }}
            onClick={handleClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider
        sx={{
          bgcolor: theme.palette.secondary.main,
          height: "1px",
          borderWidth: 0,
        }}
      />
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          border: "none",
          gap: 3,
          color: theme.palette.primary.contrastText,
        }}
      >
        <Typography
          variant="h6"
          sx={{ mt: 3, color: theme.palette.secondary.contrastText }}
        >
          Profile
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 3,
          }}
        >
          <FixedWidthBox label="Initiative ID" value={initiative?.uid} />
          <FixedWidthBox
            label="PD Lock"
            value={initiative?.pdLockstamp ?? ""}
          />
          <Box
            sx={{ display: "flex", flexDirection: "column", width: "400px" }}
          >
            <Typography variant="subtitle1">Status</Typography>
            <TableChipCell
              cardProps={{
                sx: {
                  "@media print": {
                    "-webkit-print-color-adjust": "exact",
                  },
                },
              }}
              body={
                getColorsAndTextForInitiativePhaseChips(
                  initiative?.initiativePhase as unknown as Phase
                )?.label ?? ""
              }
              chipProps={{
                sx: {
                  backgroundColor: getColorsAndTextForInitiativePhaseChips(
                    initiative?.initiativePhase as unknown as Phase
                  )?.chipColor,
                  color: getColorsAndTextForInitiativePhaseChips(
                    initiative?.initiativePhase as unknown as Phase
                  )?.textColor,
                },
              }}
            ></TableChipCell>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography variant="subtitle1">Description</Typography>

          <Typography component="div" sx={{ fontSize: "14px !important" }}>
            <Markdown fontSizeRelativeTo="parent">
              {initiative?.initiativeDescription ?? ""}
            </Markdown>
          </Typography>
        </Box>

        <Typography
          variant="h4"
          sx={{ mt: 3, color: theme.palette.secondary.contrastText }}
        >
          Scope & Ownership
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <FixedWidthBox
            label="IT Change Required"
            value={initiative?.itChangeRequired}
          />
          <FixedWidthBox
            label="IT Project ID"
            value={initiative?.itProjectId}
          />
          {showOeProgramFields && (
            <FixedWidthBox label="OE Program" value={initiative?.oeProgram} />
          )}
          {showOeProgramFields && (
            <FixedWidthBox
              label="OE Program #"
              value={initiative?.oeProgramNr}
            />
          )}
          <FixedWidthBox
            label="Affected Team/Department"
            value={initiative?.affectedTeam}
          />
          <FixedWidthBox
            label="Primary Function Affected"
            value={initiative?.primaryFunction}
          />
          <FixedWidthBox
            label="Primary Subfunction Affected"
            value={initiative?.primarySubFunction}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <FixedWidthBox
            label="Secondary Functions Affected"
            value={(initiative?.secondaryFunctions ?? []).join(", ")}
          />
          <FixedWidthBox
            label="Primary LoB Affected"
            value={initiative?.primaryLineOfBusiness}
          />
          {initiative.primaryLineOfBusiness === "Other" && (
            <FixedWidthBox
              label="Primary LoB Other"
              value={initiative?.primaryLineOfBusinessOther}
            />
          )}
          {initiative.primaryLineOfBusiness === "Commercial" && (
            <FixedWidthBox
              label="Commercial LoB"
              value={initiative?.commercialLineOfBusiness}
            />
          )}
          <FixedWidthBox
            label="Secondary LoB(s) Affected"
            value={(initiative?.secondaryLineOfBusiness ?? []).join(", ")}
          />
          {initiative.secondaryLineOfBusiness?.includes("Other") && (
            <FixedWidthBox
              label="Secondary LoB Other"
              value={initiative?.secondaryLineOfBusinessOther}
            />
          )}
          {showInterOeRegionField && (
            <FixedWidthBox
              label="Inter-OE Region"
              value={initiative?.interOeRegion}
            />
          )}
          {showBusinessUnitField && (
            <FixedWidthBox
              label="Business Unit"
              value={initiative?.businessUnit}
            />
          )}
          {showCategoryField && (
            <FixedWidthBox label="Category" value={initiative?.category} />
          )}
          <FixedWidthBox
            label="Initiative Sponsor(s)"
            value={initiative?.initiativeSponsors}
          />
        </Box>

        <FixedWidthBox
          label="Initiative Owner"
          value={(initiative?.initiativeOwner ?? []).join(", ")}
        />
        <FixedWidthBox
          label="Implementation Program"
          value={initiative?.implementationProgram}
        />

        <Typography
          variant="h4"
          sx={{ mt: 3, color: theme.palette.secondary.contrastText }}
        >
          Enablers
        </Typography>
        <FixedWidthBox
          label="Initiative Enablers"
          value={(initiative?.initiativeEnablers ?? []).join(", ")}
        />

        {initiative?.initiativeEnablers?.includes("Process Mining") && (
          <>
            <FixedWidthBox
              label="Process Mining Enablement Type"
              value={initiative?.processMiningEnablementType}
            />
          </>
        )}
        {initiative?.initiativeEnablers?.includes("Automation") && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <FixedWidthBox
                label="Automation AI Powered"
                value={initiative?.automationAIPowered}
              />
              <FixedWidthBox
                label="Automation Gen AI Powered"
                value={initiative?.automationGenAIPowered}
              />
              <FixedWidthBox
                label="Automation Complexity"
                value={initiative?.automationComplexity}
              />
              <FixedWidthBox
                label="Automation Involved System"
                value={initiative?.automationInvolvedSystem}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <FixedWidthBox
                label="Automation Methodology"
                value={initiative?.automationMethodology}
              />
              <FixedWidthBox
                label="Automation Solution Implementation Provider"
                value={initiative?.automationSolutionImplementationProvider}
              />
              <FixedWidthBox
                label="Automation Technical Solution"
                value={initiative?.automationTechnicalSolution}
              />
              <FixedWidthBox
                label="Automation Underlying Platform"
                value={initiative?.automationUnderlyingPlatform}
              />
            </Box>
          </>
        )}

        <Typography
          variant="h4"
          sx={{ mt: 3, color: theme.palette.secondary.contrastText }}
        >
          Additional Information
        </Typography>
        <FixedWidthBox
          label="KPI Dashboard Link"
          value={initiative?.relatedKPIDashboardHyperlink}
        />
        <FixedWidthBox
          label="Local Initiative Cluster"
          value={initiative?.localInitiativeCluster}
        />
        <FixedWidthBox
          label="Supporting Documents"
          value={initiative?.localInitiativeCluster}
        />
        <Box>
          {(initiative.supportingDocuments ?? []).map((doc, index) => (
            <SupportingDocument doc={doc} documentIndex={index} readOnly />
          ))}
        </Box>
        <Typography
          variant="h4"
          sx={{
            mt: 3,
            color: theme.palette.secondary.contrastText,
            "@media print": {
              pageBreakBefore: "always",
            },
          }}
        >
          Impact Plan
        </Typography>
        <Box>
          {initiative?.applicableImpactSeries?.map((impactSeries, index) => {
            return (
              <CreateTable
                key={`${impactSeries}-${index}`}
                initiative={initiative}
                seriesKey={
                  impactSeries as
                    | "tieServiceExpensesImpact"
                    | "indemnityImpact"
                    | "toplineImpact"
                    | "commissionsImpact"
                    | "internalFteImpact"
                    | "externalFteImpact"
                    | "investment"
                }
                currency={currency}
                isOnPrint={isOnPrint}
              />
            );
          })}
        </Box>
        <Typography
          variant="h4"
          sx={{ mt: 3, color: theme.palette.secondary.contrastText }}
        >
          Other Impact Metrics
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <FixedWidthBox
            label="Primary Impact Driver"
            value={initiative?.primaryImpactDriver}
          />
          <FixedWidthBox
            label="Qualitative Impact Type"
            value={initiative?.qualitativeImpactType}
          />
          <FixedWidthBox
            label={`One Off Investment (in k ${currency ?? "EUR"})`}
            value={initiative?.oneOffInvestment?.toString()}
          />
        </Box>
        <FixedWidthBox label="Baseline Year" value={initiative?.baselineYear} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <FixedWidthBox
            label="Sign off (COO)"
            value={initiative?.signOffCoo}
          />
          <FixedWidthBox
            label="Sign off (CFO)"
            value={initiative?.signOffCfo}
          />
          <FixedWidthBox
            label="Sign off (CHRO)"
            value={initiative?.signOffChro}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Comment</Typography>
          <Typography component="div" sx={{ fontSize: "14px !important" }}>
            <Markdown fontSizeRelativeTo="parent">
              {initiative?.impactComment ?? ""}
            </Markdown>
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const CreateTable = ({
  initiative,
  seriesKey,
  isOnPrint,
  currency,
}: {
  initiative: Initiative;
  seriesKey:
    | "tieServiceExpensesImpact"
    | "indemnityImpact"
    | "toplineImpact"
    | "commissionsImpact"
    | "internalFteImpact"
    | "externalFteImpact"
    | "investment";
  isOnPrint?: boolean;
  currency?: Currency;
}) => {
  const countPerYear = getCountPerYear(initiative, seriesKey);
  const includesHrCost =
    seriesKey === "tieServiceExpensesImpact" &&
    initiative.tieServiceExpensesIncludesHrCosts;
  const hrCostCountPerYear = includesHrCost
    ? getCountPerYear(initiative, "hrCostImpact")
    : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        mt: 3,
      }}
    >
      <Typography variant="subtitle1">
        {
          IMPACT_SERIES_IDS_AND_LABELS.find((item) => item.id === seriesKey)
            ?.label
        }
        {seriesKey === "externalFteImpact" || seriesKey === "internalFteImpact"
          ? ""
          : ` (in K ${currency ?? "EUR"})`}
      </Typography>
      {countPerYear.map((year, countPerYearIndex) => {
        return (
          <Table key={`${year.year}-${seriesKey}`}>
            {!isOnPrint ? (
              <>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>{year.year}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Jan</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Feb</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Mar</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Apr</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>May</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Jun</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Jul</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Aug</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Sep</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Oct</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Nov</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Dec</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{"Planned"}</TableCell>
                  {Array.from({ length: 12 }).map((_, index) => {
                    return (
                      <TableCell>
                        {seriesKey === "externalFteImpact" ||
                        seriesKey === "internalFteImpact"
                          ? year.seriesPlanned
                              ?.find((impact) => impact.month === index)
                              ?.value.toLocaleString("en-US")
                          : convertEuroToKiloEuro(
                              year.seriesPlanned?.find(
                                (impact) => impact.month === index
                              )?.value
                            )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {includesHrCost && (
                  <TableRow>
                    <TableCell>{"Planned t/o HR Expenses"}</TableCell>
                    {Array.from({ length: 12 }).map((_, index) => {
                      return (
                        <TableCell>
                          {convertEuroToKiloEuro(
                            hrCostCountPerYear[
                              countPerYearIndex
                            ].seriesPlanned?.find(
                              (impact) => impact.month === index
                            )?.value
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}
                <TableRow>
                  <TableCell>{"Actual/Forecast"}</TableCell>
                  {Array.from({ length: 12 }).map((_, index) => {
                    return (
                      <TableCell>
                        {seriesKey === "externalFteImpact" ||
                        seriesKey === "internalFteImpact"
                          ? year.seriesActual
                              ?.find((impact) => impact.month === index)
                              ?.value.toLocaleString("en-US")
                          : convertEuroToKiloEuro(
                              year.seriesActual?.find(
                                (impact) => impact.month === index
                              )?.value
                            )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {includesHrCost && (
                  <TableRow>
                    <TableCell>{"Actual t/o HR Expenses"}</TableCell>
                    {Array.from({ length: 12 }).map((_, index) => {
                      return (
                        <TableCell>
                          {convertEuroToKiloEuro(
                            hrCostCountPerYear[
                              countPerYearIndex
                            ].seriesActual?.find(
                              (impact) => impact.month === index
                            )?.value
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}
              </>
            ) : (
              <>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>{year.year}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Jan</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Feb</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Mar</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Apr</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>May</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Jun</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{"Planned"}</TableCell>
                  {[0, 1, 2, 3, 4, 5].map((_, index) => {
                    return (
                      <TableCell>
                        {convertEuroToKiloEuro(
                          year.seriesPlanned?.find(
                            (impact) => impact.month === index
                          )?.value
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow>
                  <TableCell>{"Actual/Forecast"}</TableCell>
                  {[0, 1, 2, 3, 4, 5].map((val) => {
                    return (
                      <TableCell>
                        {convertEuroToKiloEuro(
                          year.seriesActual?.find(
                            (impact) => impact.month === val
                          )?.value
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow
                  sx={{
                    marginTop: "16px",
                    height: 70,
                    verticalAlign: "bottom",
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>{year.year}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Jul</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Aug</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Sep</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Oct</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Nov</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Dec</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{"Planned"}</TableCell>
                  {[6, 7, 8, 9, 10, 11].map((val) => {
                    return (
                      <TableCell>
                        {convertEuroToKiloEuro(
                          year.seriesPlanned?.find(
                            (impact) => impact.month === val
                          )?.value
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>

                <TableRow>
                  <TableCell>{"Actual/Forecast"}</TableCell>
                  {[6, 7, 8, 9, 10, 11].map((_, index) => {
                    return (
                      <TableCell>
                        {convertEuroToKiloEuro(
                          year.seriesActual?.find(
                            (impact) => impact.month === index
                          )?.value
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </>
            )}
          </Table>
        );
      })}
    </Box>
  );
};

function convertEuroToKiloEuro(value: number | undefined) {
  if (!value) {
    return "";
  }
  return (value / 1000).toLocaleString("en-US");
}

function getCountPerYear(
  initiative: Initiative,
  seriesKey:
    | "tieServiceExpensesImpact"
    | "hrCostImpact"
    | "indemnityImpact"
    | "toplineImpact"
    | "commissionsImpact"
    | "internalFteImpact"
    | "externalFteImpact"
    | "investment"
) {
  const allYears = Array.from(
    new Set(
      (initiative[`${seriesKey}Planned`] as unknown as MonthlyImpact[])
        .map((i) => i.year)
        .concat(
          (initiative[`${seriesKey}Actual`] as unknown as MonthlyImpact[]).map(
            (i) => i.year
          )
        )
    )
  ).sort();

  return allYears
    .map((year) => {
      if (
        !initiative[`${seriesKey}Planned`] ||
        !initiative[`${seriesKey}Actual`] ||
        !Array.isArray(initiative[`${seriesKey}Planned`]) ||
        !Array.isArray(initiative[`${seriesKey}Actual`])
      ) {
        return {
          planned: [],
          actual: [],
          year,
        };
      }
      return {
        seriesPlanned: (
          initiative[`${seriesKey}Planned`] as unknown as MonthlyImpact[]
        ).filter((i) => i.year === year),
        seriesActual: (
          initiative[`${seriesKey}Actual`] as unknown as MonthlyImpact[]
        ).filter((i) => i.year === year),
        year,
      };
    })
    .filter(
      (i) =>
        (i.seriesPlanned && i.seriesPlanned?.length > 0) ||
        (i.seriesActual && i.seriesActual?.length > 0)
    );
}
